/**
 * Slim banner shown to iOS users on Chrome/Firefox/Edge (Path C in the UX plan).
 *
 * iOS only allows PWA installs from Safari, so the most useful thing we can do
 * is tell the user that and let them copy the URL with one tap.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { Copy, X } from 'lucide-react'
import { toast } from 'sonner'
import { usePromptMemory } from '@/hooks/pwa'

interface IosChromeBannerProps {
  show: boolean
  onDismiss: () => void
}

export function IosChromeBanner({ show, onDismiss }: IosChromeBannerProps) {
  const memory = usePromptMemory('ios-chrome', 30)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.success('Link copied — open Safari and paste to install.')
      memory.dismiss()
      onDismiss()
    } catch {
      toast.error("Couldn't copy. Open this URL in Safari to install.")
    }
  }

  const handleClose = () => {
    memory.dismiss()
    onDismiss()
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          role="region"
          aria-label="Install instructions"
          initial={{ y: -32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -32, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          className="sticky top-0 z-[55] border-b border-primary/30 bg-primary/10 px-4 py-2 text-sm text-foreground backdrop-blur"
        >
          <div className="flex items-center gap-3">
            <p className="flex-1 leading-snug">
              Install Taqat? On iOS, installing only works in <span className="font-semibold">Safari</span>.
            </p>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy link
            </button>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Dismiss"
              className="rounded-md p-1 text-muted-foreground hover:bg-primary/20"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
